import { BaseApi } from "api/BaseApi.ts";
import { TenantBaseApi } from "api/TenantSlug/TenantBaseApi.ts";

import StoreSettingRequest = App.Http.Requests.Setting.StoreSettingRequest;
import UpdateWorkspaceCurrencyRequest = App.Http.Requests.Setting.UpdateWorkspaceCurrencyRequest;
import GetSettingResponse = App.Http.Responses.Setting.GetSettingResponse;
import UpdateSettingsRequest = App.Http.Requests.Setting.UpdateSettingsRequest;
import GetSettingsResponse = App.Http.Responses.Setting.GetSettingsResponse;
import GetSettingsDefaultsResponse = App.Http.Responses.Setting.GetSettingsDefaultsResponse;
import GetWorkspaceCurrencyResponse = App.Http.Responses.Setting.GetWorkspaceCurrencyResponse;

class SettingApi extends TenantBaseApi {
    /**
     * Get list of Settings
     *
     * - Request: -
     * - Response: GetSettingsResponse
     */
    async index(): Promise<GetSettingsResponse> {
        return await this.get<GetSettingsResponse, void>("/settings");
    }

    /**
     * Store Setting
     *
     * - Request: StoreSettingRequest
     * - Response: GetSettingResponse
     */
    async store(request: StoreSettingRequest): Promise<GetSettingResponse> {
        return await this.post<GetSettingResponse, StoreSettingRequest>("/settings", request);
    }

    /**
     * Get specific Setting
     *
     * - Request: { key: string }
     * - Response: GetSettingResponse
     */
    async show(key: string): Promise<GetSettingResponse> {
        return await this.get<GetSettingResponse, void>(`/settings/${key}`);
    }

    /**
     * Update Setting
     *
     * - Request: UpdateSettingsRequest
     * - Response: GetSettingsResponse
     */
    async update(request: UpdateSettingsRequest): Promise<GetSettingsResponse> {
        return await this.patch<GetSettingsResponse, UpdateSettingsRequest>("/settings", request);
    }

    /**
     * Delete Setting
     *
     * - Request: { key: string }
     * - Response: GetSettingResponse
     */
    async destroy(key: string): Promise<GetSettingResponse> {
        return await this.delete<GetSettingResponse, void>(`/settings/${key}`);
    }
}

class WorkspaceCurrencyApi extends TenantBaseApi {
    /**
     * Get current workspace currency + reconversion/cooldown status
     *
     * - Request: -
     * - Response: GetWorkspaceCurrencyResponse
     */
    async show(): Promise<GetWorkspaceCurrencyResponse> {
        return await this.get<GetWorkspaceCurrencyResponse, void>("/settings/currency");
    }

    /**
     * Update workspace currency (triggers a background report reconversion)
     *
     * - Request: UpdateWorkspaceCurrencyRequest
     * - Response: GetWorkspaceCurrencyResponse
     */
    async update(request: UpdateWorkspaceCurrencyRequest): Promise<GetWorkspaceCurrencyResponse> {
        return await this.post<GetWorkspaceCurrencyResponse, UpdateWorkspaceCurrencyRequest>(
            "/settings/currency",
            request
        );
    }
}

class DefaultsSettingApi extends BaseApi {
    /**
     * Get defaults
     *
     * - Request: -
     * - Response: GetSettingsResponse
     */
    async defaults(tenantSlug: string, silentError = false): Promise<GetSettingsDefaultsResponse> {
        return await this.get<GetSettingsDefaultsResponse, void>(
            `${tenantSlug}/settings/defaults`,
            silentError ? { silentError } : undefined
        );
    }
}
export const settingApi = new SettingApi();
export const defaultsSettingApi = new DefaultsSettingApi();
export const workspaceCurrencyApi = new WorkspaceCurrencyApi();
